import { Button } from "@components/shadcn-ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@components/shadcn-ui/dialog";
import { Form } from "@components/shadcn-ui/form";
import { toast } from "@customHooks/useToast";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { Action } from "features/Permission/types";
import { applicationService } from "features/createApplication/service/applicationService";
import { useNavigate } from "react-router-dom";

const REJECTION_REASONS: Record<string, string> = {
  CUSTOMER_NOT_INTERESTED: "Customer not interested",
  BOUNCE_IN_BANK_STATEMENT: "Bounce in bank statement",
  BANK_STATEMENT_INCOMPLETE: "Incomplete bank statement",
  APPLICATION_FORM_INCOMPLETE: "Incomplete application form",
  KYC_NOT_CLEAR: "KYC not clear",
  KYC_NOT_PROPER: "KYC not proper",
  INVALID_ADDRESS_PROOF: "Invalid address proof",
  BAD_BUREAU_TRADELINES: "Negative bureau tradelines",
  BAD_BUREAU_SCORE: "Low bureau score",
  NEGATIVE_TVR: "Negative TVR",
  NEGATIVE_RESIDENCE_VERIFICATION: "Negative residence verification",
  NEGATIVE_PROJECT_VERIFICATION: "Negative project verification",
  CUSTOMER_NOT_ELIGIBLE: "Customer not eligible for loan",
  CUSTOMER_NOT_APPLIED_FOR_LOAN: "Customer did not apply for loan",
  APPLICANT_AND_DOCUMENT_MISMATCH: "Applicant and document mismatch",
  INVALID_OR_WRONG_ITR: "Invalid / Wrong ITR",
  OTHER: "Other",
};

interface Application {
  id: string;
  leadId: string;
  product: string;
  state: string;
}

interface RejectApplicationModalProps {
  application: Application;
}

export function RejectApplicationModal({
  application,
}: RejectApplicationModalProps) {
  const [isModalOpen, setModalOpen] = useState(false);
  const form = useForm({
    defaultValues: {
      rejectReason: "",
      detailedReason: "",
    },
  });
  const navigate = useNavigate();

  const selectedRejectReason = form.watch("rejectReason");
  const detailedReason = form.watch("detailedReason");

  const handleRejectApplication = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    if (!selectedRejectReason) {
      toast({
        variant: "default",
        description: "Please select a rejection reason.",
      });
      return;
    }

    if (selectedRejectReason === "OTHER" && !detailedReason?.trim()) {
      toast({
        variant: "default",
        description: "Please provide a detailed reason for rejection.",
      });
      return;
    }

    const payload = {
      id: application.id,
      productType: application.product,
      rejectionDetails: {
        rejectReason: selectedRejectReason,
        detailedReason: selectedRejectReason === "OTHER" ? detailedReason : "",
      },
    };

    try {
      await applicationService.performAction(
        payload,
        Action.REJECT_APPLICATION,
        application.leadId
      );
      toast({
        variant: "default",
        description: "Application has been rejected successfully!",
      });
      navigate("/dashboard/applications");

      form.reset();
      setModalOpen(false);
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in rejecting application:", error);
    }
  };

  const isRejectDisabled =
    !selectedRejectReason ||
    (selectedRejectReason === "OTHER" && !detailedReason?.trim());

  return (
    <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Reject</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader className="p-4 md:p-0">
          <h2 className="text-center text-lg font-semibold">
            Reject Application
          </h2>
        </DialogHeader>
        <Form {...form}>
          <div className="flex flex-col gap-4 px-4">
            <div className="flex flex-col">
              <label htmlFor="rejectReason" className="mb-1 font-medium">
                Rejection Reason
              </label>
              <select
                id="rejectReason"
                className="border p-2 rounded"
                {...form.register("rejectReason", { required: true })}
              >
                <option value="">Select a reason</option>
                {Object.entries(REJECTION_REASONS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {selectedRejectReason === "OTHER" && (
              <div className="flex flex-col">
                <label htmlFor="detailedReason" className="mb-1 font-medium">
                  Detailed Reason
                </label>
                <input
                  id="detailedReason"
                  type="text"
                  placeholder="Enter detailed reason"
                  className="border p-2 rounded"
                  {...form.register("detailedReason", {
                    required: selectedRejectReason === "OTHER",
                  })}
                />
              </div>
            )}
          </div>
        </Form>
        <DialogFooter className="my-4 md:my-0 flex justify-center flex-row gap-4">
          <Button onClick={handleRejectApplication} disabled={isRejectDisabled}>
            Reject
          </Button>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
